import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { CalendarRange, ArrowRight, X, Sparkles } from 'lucide-react'
import { useTranslation } from '../../hooks/use-i18n'

type HighlightId =
  | 'championsLeague'
  | 'premierLeague'
  | 'laLiga'
  | 'ligue1'
  | 'serieA'
  | 'bundesliga'
  | 'formula1'

type CarouselItem = {
  id: HighlightId
  accent: string
  glow: string
  border: string
  emblem: 'ucl' | 'f1' | 'flag'
  flagSrc?: string
  flagAlt?: string
  logoSrc?: string
}

const UCL_LOGO = `${process.env.PUBLIC_URL || ''}/ucl-logo.png`
const F1_LOGO = `${process.env.PUBLIC_URL || ''}/f1-logo.png`

const CAROUSEL_ITEMS: CarouselItem[] = [
  {
    id: 'championsLeague',
    accent: 'from-[#0A1F4D]/95 via-[#061433]/90 to-slate-950/90',
    glow: 'rgba(180, 210, 255, 0.35)',
    border: 'rgba(190, 210, 255, 0.4)',
    emblem: 'ucl',
    logoSrc: UCL_LOGO,
  },
  {
    id: 'premierLeague',
    accent: 'from-[#3D195B]/90 via-[#1a0b2e]/80 to-slate-950/80',
    glow: 'rgba(0,255,133,0.28)',
    border: 'rgba(0,255,133,0.35)',
    emblem: 'flag',
    flagSrc: 'https://flagcdn.com/w160/gb-eng.png',
    flagAlt: 'England',
  },
  {
    id: 'laLiga',
    accent: 'from-[#EE4749]/80 via-[#7a1d22]/75 to-slate-950/80',
    glow: 'rgba(255,191,0,0.32)',
    border: 'rgba(255,191,0,0.4)',
    emblem: 'flag',
    flagSrc: 'https://flagcdn.com/w160/es.png',
    flagAlt: 'Spain',
  },
  {
    id: 'ligue1',
    accent: 'from-[#091C3E]/95 via-[#1b3a7a]/70 to-slate-950/80',
    glow: 'rgba(218,224,37,0.28)',
    border: 'rgba(218,224,37,0.4)',
    emblem: 'flag',
    flagSrc: 'https://flagcdn.com/w160/fr.png',
    flagAlt: 'France',
  },
  {
    id: 'serieA',
    accent: 'from-[#024494]/90 via-[#012a5c]/80 to-slate-950/80',
    glow: 'rgba(126,200,255,0.3)',
    border: 'rgba(126,200,255,0.4)',
    emblem: 'flag',
    flagSrc: 'https://flagcdn.com/w160/it.png',
    flagAlt: 'Italy',
  },
  {
    id: 'bundesliga',
    accent: 'from-[#D20515]/85 via-[#6b0810]/80 to-slate-950/80',
    glow: 'rgba(255,255,255,0.22)',
    border: 'rgba(255,255,255,0.3)',
    emblem: 'flag',
    flagSrc: 'https://flagcdn.com/w160/de.png',
    flagAlt: 'Germany',
  },
  {
    id: 'formula1',
    accent: 'from-[#E10600]/90 via-[#1a0505]/85 to-zinc-950/90',
    glow: 'rgba(225,6,0,0.4)',
    border: 'rgba(255,80,80,0.45)',
    emblem: 'f1',
    logoSrc: F1_LOGO,
  },
]

function circularOffset(index: number, active: number, count: number) {
  let delta = index - active
  const half = count / 2
  if (delta > half) delta -= count
  if (delta < -half) delta += count
  return delta
}

function splitSeasonDates(dates: string) {
  const parts = dates.split(/\s*[–—-]\s*/)
  if (parts.length === 2 && parts[0] && parts[1]) {
    return { start: parts[0], end: parts[1] }
  }
  return { start: dates, end: null as string | null }
}

/**
 * 2026–27 Big Five + Champions League + Formula 1 season carousel, with detail modals.
 */
const LeagueSeasonBanners: React.FC = () => {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<HighlightId | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [carouselPaused, setCarouselPaused] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)
  const [isNarrow, setIsNarrow] = useState(true)
  const openItem = CAROUSEL_ITEMS.find((item) => item.id === openId) ?? null
  const f1Open = openId === 'formula1'
  const modalOpen = Boolean(openItem)
  const itemCount = CAROUSEL_ITEMS.length
  const centerScale = isNarrow ? 1.08 : 1.12
  const sideScale = isNarrow ? 0.78 : 0.86
  const slideAmount = isNarrow ? 96 : 70

  useEffect(() => {
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const widthMq = window.matchMedia('(max-width: 639px)')
    const sync = () => {
      setReduceMotion(motionMq.matches)
      setIsNarrow(widthMq.matches)
    }
    sync()
    motionMq.addEventListener('change', sync)
    widthMq.addEventListener('change', sync)
    return () => {
      motionMq.removeEventListener('change', sync)
      widthMq.removeEventListener('change', sync)
    }
  }, [])

  useEffect(() => {
    if (reduceMotion || modalOpen || carouselPaused) return
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % itemCount)
    }, 2000)
    return () => window.clearInterval(id)
  }, [reduceMotion, modalOpen, carouselPaused, itemCount])

  useEffect(() => {
    if (!openId) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [openId])

  useEffect(() => {
    if (!openId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openId])

  const close = () => setOpenId(null)

  return (
    <>
      <motion.div
        variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
        className="w-full max-w-4xl mx-auto mb-3 sm:mb-4 min-w-0"
      >
        <p className="flex justify-center mb-3 sm:mb-3.5">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2"
            style={{
              backgroundColor: 'rgba(125, 211, 252, 0.12)',
              border: '1px solid rgba(125, 211, 252, 0.32)',
            }}
          >
            <Sparkles className="w-4 h-4 sm:w-[1.15rem] sm:h-[1.15rem] text-sky-300 shrink-0" aria-hidden />
            <span className="font-display text-[0.92rem] sm:text-[1.05rem] font-bold tracking-wide leading-none">
              <span className="text-sky-300 [text-shadow:0_0_10px_rgba(125,211,252,0.45)]">2026–2027</span>
              {' '}
              <span className="text-white/95">{t('landing.leagues.kickerSeason')}</span>
            </span>
          </span>
        </p>

        <div
          className="relative h-[13.75rem] xs:h-[14.25rem] sm:h-[12rem] md:h-[12.5rem]"
          onMouseEnter={() => setCarouselPaused(true)}
          onMouseLeave={() => setCarouselPaused(false)}
          onFocusCapture={() => setCarouselPaused(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setCarouselPaused(false)
            }
          }}
        >
          <div
            className="absolute inset-x-0 top-0 bottom-6 overflow-hidden px-1 xs:px-2"
            aria-roledescription="carousel"
            aria-label={t('landing.leagues.kicker')}
          >
            {CAROUSEL_ITEMS.map((item, index) => {
              const offset = circularOffset(index, activeIndex, itemCount)
              const isCenter = offset === 0
              const visible = Math.abs(offset) <= 1
              return (
                <motion.div
                  key={item.id}
                  className="absolute top-1/2 left-1/2 w-[min(52%,11.75rem)] xs:w-[min(48%,12rem)] sm:w-[min(48%,16rem)] md:w-[min(40%,15.5rem)]"
                  initial={false}
                  animate={{
                    x: `calc(-50% + ${offset * slideAmount}%)`,
                    y: '-50%',
                    scale: isCenter ? centerScale : sideScale,
                    opacity: visible ? 1 : 0,
                    zIndex: 10 - Math.abs(offset),
                  }}
                  transition={
                    reduceMotion
                      ? { duration: 0 }
                      : { type: 'spring', stiffness: 280, damping: 28 }
                  }
                  style={{ pointerEvents: visible ? 'auto' : 'none' }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (!isCenter) {
                        setActiveIndex(index)
                        return
                      }
                      setOpenId(item.id)
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={openId === item.id}
                    aria-current={isCenter ? 'true' : undefined}
                    aria-label={t('landing.leagues.openModalAria', {
                      name: t(`landing.leagues.${item.id}.name`),
                    })}
                    className="group relative flex flex-col w-full aspect-square sm:aspect-auto sm:min-h-[6.75rem] rounded-xl sm:rounded-2xl overflow-hidden border border-white/15 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/90"
                    style={{
                      boxShadow: isCenter
                        ? `0 16px 36px -10px ${item.glow}`
                        : `0 10px 28px -12px ${item.glow}`,
                    }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.accent}`} />
                    {item.emblem === 'ucl' && (
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_40%,rgba(255,255,255,0.12),transparent_42%)]" />
                    )}
                    {item.emblem === 'f1' && (
                      <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent_0_10px,rgba(255,255,255,0.03)_10px_12px)] opacity-50" />
                    )}
                    <div className="absolute inset-0 bg-white/[0.04] group-hover:bg-white/[0.08] transition-colors" />

                    <div className="relative z-10 p-2.5 sm:p-3 flex flex-col gap-1.5 min-h-0 h-full justify-between">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 pr-1">
                          {item.emblem === 'ucl' && (
                            <span className="inline-flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-white/70">
                              <img
                                src={item.logoSrc}
                                alt=""
                                width={36}
                                height={34}
                                className="h-6 w-6 sm:h-7 sm:w-7 object-contain"
                              />
                            </span>
                          )}
                          {item.emblem === 'f1' && (
                            <img
                              src={item.logoSrc}
                              alt=""
                              width={80}
                              height={20}
                              className="h-4 sm:h-5 w-auto shrink-0 object-contain"
                            />
                          )}
                          <p className="font-display font-bold text-white text-[13px] sm:text-sm leading-snug [text-shadow:0_1px_2px_rgba(0,0,0,0.4)]">
                            {t(`landing.leagues.${item.id}.name`)}
                          </p>
                        </div>
                        {item.emblem === 'flag' && item.flagSrc && (
                          <img
                            src={item.flagSrc}
                            alt=""
                            width={32}
                            height={24}
                            className="h-5 w-7 shrink-0 rounded-[3px] object-cover ring-1 ring-white/25 shadow"
                            loading="lazy"
                          />
                        )}
                      </div>

                      {(() => {
                        const { start, end } = splitSeasonDates(t(`landing.leagues.${item.id}.dates`))
                        return (
                          <span className="inline-flex items-center gap-1.5 self-start max-w-full rounded-lg bg-black/30 ring-1 ring-white/20 px-2 py-1.5 text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]">
                            <CalendarRange className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 text-sky-200" aria-hidden />
                            <span className="flex flex-col min-w-0 text-left leading-tight">
                              <span className="text-[12px] sm:text-[13px] font-semibold tracking-wide">{start}</span>
                              {end ? (
                                <span className="text-[11px] sm:text-xs font-medium text-white/85 tracking-wide">– {end}</span>
                              ) : null}
                            </span>
                          </span>
                        )
                      })()}

                      <span className="inline-flex items-center gap-0.5 text-[10px] sm:text-[11px] font-semibold text-white/85 group-hover:text-white">
                        {t('landing.leagues.openDetails')}
                        <ArrowRight className="w-3 h-3" aria-hidden />
                      </span>
                    </div>
                  </button>
                </motion.div>
              )
            })}
          </div>

          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1.5 z-20">
            {CAROUSEL_ITEMS.map((item, index) => (
              <button
                key={`${item.id}-dot`}
                type="button"
                aria-label={t(`landing.leagues.${item.id}.name`)}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => setActiveIndex(index)}
                className={`h-1.5 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
                  index === activeIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/35 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {modalOpen && openItem && (
          <motion.div
            key={openId ?? 'season-modal'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[52] flex items-end xs:items-start justify-center px-3 xs:px-4 pt-0 xs:pt-[4.75rem] pb-0 xs:pb-6 sm:pt-24 sm:pb-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="league-modal-title"
            aria-describedby="league-modal-dates"
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/60 backdrop-blur-md cursor-default border-0 p-0 w-full h-full"
              aria-label={t('landing.leagues.closeBackdropAria')}
              onClick={close}
            />

            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="relative z-10 w-full max-w-lg sm:max-w-xl max-h-[92dvh] xs:max-h-[min(88dvh,40rem)] overflow-y-auto overscroll-contain rounded-t-2xl xs:rounded-2xl sm:rounded-3xl shadow-2xl"
              style={{ border: `1px solid ${openItem.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${openItem.accent}`} />
              <div
                className="pointer-events-none absolute inset-0 opacity-95"
                style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
              />

              <button
                type="button"
                onClick={close}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/55 border border-white/15 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                aria-label={t('landing.leagues.closeModal')}
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative z-10 p-5 pt-12 sm:p-8 flex flex-col items-center text-center">
                {openItem.emblem === 'ucl' ? (
                  <span className="inline-flex h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem] items-center justify-center rounded-2xl bg-white shadow-lg mb-3 sm:mb-4">
                    <img
                      src={openItem.logoSrc}
                      alt=""
                      width={72}
                      height={68}
                      className="h-14 w-14 sm:h-16 sm:w-16 object-contain"
                    />
                  </span>
                ) : openItem.emblem === 'f1' ? (
                  <img
                    src={openItem.logoSrc}
                    alt=""
                    width={240}
                    height={60}
                    className="h-8 sm:h-10 w-auto object-contain mb-3 sm:mb-4"
                  />
                ) : (
                  <img
                    src={openItem.flagSrc}
                    alt={openItem.flagAlt}
                    width={72}
                    height={54}
                    className="h-10 w-14 sm:h-12 sm:w-[4.25rem] rounded-md object-cover ring-1 ring-white/30 shadow-lg mb-3 sm:mb-4"
                  />
                )}

                <h2
                  id="league-modal-title"
                  className="font-display font-bold text-lg sm:text-2xl text-white mb-1.5 px-2 [text-shadow:0_2px_8px_rgba(0,0,0,0.4)]"
                >
                  {t(`landing.leagues.${openItem.id}.name`)}
                </h2>
                <p
                  id="league-modal-dates"
                  className="inline-flex items-center gap-1.5 text-sm sm:text-base text-white/90 font-medium mb-3 sm:mb-4"
                >
                  <CalendarRange className="w-4 h-4 opacity-90" aria-hidden />
                  {t(`landing.leagues.${openItem.id}.dates`)}
                </p>
                <p className="text-xs sm:text-sm text-white/80 max-w-md mb-5 sm:mb-7 leading-relaxed px-1">
                  {t(`landing.leagues.${openItem.id}.blurb`)}
                </p>

                <div className="flex flex-col xs:flex-row items-stretch xs:items-center justify-center gap-2.5 sm:gap-3 w-full max-w-md">
                  <Link
                    to="/login"
                    onClick={close}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white border border-white/20 shadow-md transition-transform hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg, #ff6b35 0%, #e11d48 55%, #be185d 100%)',
                      boxShadow: '0 10px 28px rgba(225, 29, 72, 0.35)',
                    }}
                  >
                    {t('landing.leagues.ctaPrimary')}
                    <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                  </Link>
                  <Link
                    to={f1Open ? '/public/formula1' : '/public/football'}
                    onClick={close}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white/95 bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur-sm transition-colors"
                  >
                    {f1Open
                      ? t('landing.leagues.ctaSecondaryF1')
                      : t('landing.leagues.ctaSecondary')}
                  </Link>
                </div>
                <p className="mt-3 text-xs text-white/65">{t('landing.hero.freeTrialSubtext')}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default LeagueSeasonBanners
